<script>
  import Layout from '../components/Layout.svelte';
  import { scadenzeApi, companyTranchesApi } from '../stores/api.js';
  import { activeCompanyId, companiesLoading } from '../stores/company.js';
  import { canWriteInCompany } from '../stores/capabilities.js';
  import { link, querystring } from 'svelte-spa-router';
  import { get } from 'svelte/store';
  import { t, tImmediate } from '../i18n/index.js';
  import {
    CalendarClock, AlertTriangle, Check, HelpCircle,
  } from '../lib/icons/index.js';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import EmptyState from '../components/EmptyState.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import PageLoader from '../components/PageLoader.svelte';
  import { formatEuro, formatDate, localizedError } from '../lib/labels.js';
  import { todayCalendarDay } from '../lib/payment.js';
  import { scadenzeNotice } from '../lib/scadenze.js';

  let data = null;
  let loading = true;
  let error = null;

  // Partial totals at the server's cap, or only the list cut short: two different warnings.
  $: notice = scadenzeNotice(data);

  const DIRECTION_VALUES = ['tutte', 'incassi', 'pagamenti'];
  const STATUS_VALUES = ['tutte', 'aperte', 'scadute', 'inScadenza', 'programmate', 'saldate'];

  // The Dashboard money cards link here with ?direction=…&status=scadute. Read
  // once, synchronously, before the loader below fires, so the first request
  // already carries the filters the card promised instead of showing everything
  // and narrowing a moment later. Anything unrecognised falls back to the
  // default view, so a hand-edited URL cannot leave a select blank.
  const initialQuery = new URLSearchParams(get(querystring) || '');
  const seed = (param, allowed, fallback) => (
    allowed.includes(initialQuery.get(param)) ? initialQuery.get(param) : fallback
  );

  // The page opens on what is not settled yet. Sorted with the arrears first,
  // that is exactly the two questions an owner has when they open it: what is
  // already late, and what is about to be.
  let filterDirection = seed('direction', DIRECTION_VALUES, 'tutte');
  let filterStatus = seed('status', STATUS_VALUES, 'aperte');
  let filterFrom = '';
  let filterTo = '';
  let filterCounterparty = '';

  /** Row currently open for "segna pagata", plus its editable amounts. */
  let payingId = null;
  let payDate = '';
  let payAmount = '';
  let paySaving = false;
  let payError = null;

  const SIDES = ['incassi', 'pagamenti'];

  /** Urgency order, so a filter that mixes buckets still reads worst-first. */
  const BUCKET_ORDER = { scadute: 0, inScadenza: 1, programmate: 2, saldate: 3 };

  const BUCKET_BADGE = {
    scadute: 'badge-red',
    inScadenza: 'badge-yellow',
    programmate: 'badge-cyan',
    saldate: 'badge-green',
  };

  $: companyId = $activeCompanyId;
  $: if (companyId) load();
  // Waiting for the company list is still loading — the "nessuna scadenza"
  // empty state would otherwise flash before the first company arrives.
  $: if (!companyId) { data = null; loading = $companiesLoading; }

  $: totals = data?.totals || null;
  $: dueSoonDays = data?.dueSoonDays ?? 7;

  // One pass over the rows instead of two filters per side, and sorted here
  // rather than trusting the order the API happened to return: the endpoint
  // orders by due date alone, which interleaves settled rows among the arrears.
  $: bySide = (() => {
    const grouped = { incassi: [], pagamenti: [] };
    for (const item of data?.items || []) {
      grouped[item.tipo === 'incasso' ? 'incassi' : 'pagamenti'].push(item);
    }
    for (const rows of Object.values(grouped)) {
      rows.sort((a, b) => (BUCKET_ORDER[a.bucket] - BUCKET_ORDER[b.bucket])
        || a.dataScadenza.localeCompare(b.dataScadenza));
    }
    return grouped;
  })();

  // A side the user filtered away is hidden entirely rather than shown empty:
  // "Da pagare — nessuna rata" next to an explicit "solo incassi" filter reads
  // as a bug.
  $: visibleSides = filterDirection === 'incassi' ? ['incassi']
    : filterDirection === 'pagamenti' ? ['pagamenti']
      : SIDES;

  let showIntro = false;

  async function load() {
    if (!companyId) return;
    loading = true;
    error = null;
    try {
      data = await scadenzeApi.list(companyId, {
        direction: filterDirection,
        status: filterStatus,
        from: filterFrom || undefined,
        to: filterTo || undefined,
        counterparty: filterCounterparty.trim() || undefined,
      });
    } catch (err) {
      error = err;
      data = null;
    } finally {
      loading = false;
    }
  }

  function resetFilters() {
    filterDirection = 'tutte';
    filterStatus = 'aperte';
    filterFrom = '';
    filterTo = '';
    filterCounterparty = '';
    load();
  }

  /** Open the inline form, pre-filled with the amount still owed. */
  function startPaying(item) {
    payingId = item.id;
    payDate = todayCalendarDay();
    payAmount = String(item.residuo > 0 ? item.residuo : item.importo);
    payError = null;
  }

  /** The catalog sentence that names what is actually wrong, or null when the
   *  form can be submitted. The banner used to show the amount field's help
   *  text, which says nothing about the date and gives no instruction. */
  function payValidationError(amount) {
    if (!payDate) return tImmediate('scadenzario.markPaid.errors.missingDate');
    if (Number.isNaN(amount)) return tImmediate('scadenzario.markPaid.errors.invalidAmount');
    if (amount <= 0) return tImmediate('scadenzario.markPaid.errors.notPositive');
    return null;
  }

  function cancelPaying() {
    payingId = null;
    payError = null;
  }

  async function confirmPaying(item) {
    const versato = Number(String(payAmount).replace(',', '.'));
    const problem = payValidationError(versato);
    if (problem) {
      payError = localizedError(problem);
      return;
    }
    paySaving = true;
    payError = null;
    try {
      // `importoPagato` is the cumulative amount paid on the instalment, so the
      // balance of a part payment is added to what was already recorded rather
      // than replacing it — otherwise settling the second half would erase the
      // first and leave the row open forever.
      const importoPagato = Math.round((item.pagato + versato) * 100) / 100;
      // The existing per-invoice tranche endpoint, deliberately reused: it is
      // the one place that already proves the invoice belongs to this company
      // before touching the instalment.
      await companyTranchesApi.update(companyId, item.invoiceId, item.id, {
        dataPagamento: payDate,
        importoPagato,
      });
      payingId = null;
      await load();
    } catch (err) {
      payError = err;
    } finally {
      paySaving = false;
    }
  }

  /** "in ritardo di 3 giorni" / "scade oggi" / "tra 5 giorni" / "saldata il …" */
  function timingLabel(item) {
    if (item.bucket === 'saldate') {
      return tImmediate('scadenzario.row.paidOn', { date: formatDate(item.dataPagamento) });
    }
    const days = item.giorniAllaScadenza;
    if (days === 0) return tImmediate('scadenzario.row.dueToday');
    if (days === 1) return tImmediate('scadenzario.row.dueTomorrow');
    if (days === -1) return tImmediate('scadenzario.row.overdueOneDay');
    if (days < 0) return tImmediate('scadenzario.row.overdueDays', { days: Math.abs(days) });
    return tImmediate('scadenzario.row.dueInDays', { days });
  }

  function counterpartyLabel(item) {
    return item.invoice.controparte
      || item.invoice.controparteTaxId
      || tImmediate('scadenzario.row.unknownCounterparty');
  }

  function invoiceLabel(item) {
    return item.invoice.invoiceNumber || tImmediate('scadenzario.row.noNumber');
  }
</script>

<Layout>
  <div class="view-head">
    <div class="view-head-copy">
      <h1 class="section-title">{$t('scadenzario.title')}</h1>
      <p class="view-subtitle">{$t('scadenzario.subtitle')}</p>
    </div>
  </div>

  <!-- Feature description. The premise of this product is that someone who has
       never used an invoicing system can learn the domain from the interface,
       so the explanation is a first-class part of the page rather than a
       tooltip. Collapsed after the first read, expanded by default while the
       page has nothing to show. -->
  <div class="mb-6">
    <button type="button" class="btn-link -ml-1"
      on:click={() => showIntro = !showIntro} aria-expanded={showIntro} aria-controls="scadenzario-intro">
      <HelpCircle size={16} aria-hidden="true" />
      {showIntro ? $t('scadenzario.intro.toggleHide') : $t('scadenzario.intro.toggleShow')}
    </button>
    {#if showIntro}
      <div id="scadenzario-intro" class="next-step mt-2 space-y-4 text-sm measure-wide">
        <h2 class="text-heading">{$t('scadenzario.intro.title')}</h2>
        <div>
          <p class="font-bold text-heading">{$t('scadenzario.intro.whatIsItTitle')}</p>
          <p class="mt-1 text-muted">{$t('scadenzario.intro.whatIsIt')}</p>
        </div>
        <div>
          <p class="font-bold text-heading">{$t('scadenzario.intro.whereFromTitle')}</p>
          <p class="mt-1 text-muted">{$t('scadenzario.intro.whereFrom')}</p>
        </div>
        <div>
          <p class="font-bold text-heading">{$t('scadenzario.intro.whyManyTitle')}</p>
          <p class="mt-1 text-muted">{$t('scadenzario.intro.whyMany')}</p>
        </div>
        <div>
          <p class="font-bold text-heading">{$t('scadenzario.intro.howToUseTitle')}</p>
          <p class="mt-1 text-muted">{$t('scadenzario.intro.howToUse')}</p>
        </div>
      </div>
    {/if}
  </div>

  <!-- Filters -->
  <div class="mb-6 pb-4 border-b-w" style="border-color: var(--color-border-default);">
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      <div>
        <label class="label" for="sc-direction">{$t('scadenzario.filters.direction')}</label>
        <select id="sc-direction" class="input w-full" bind:value={filterDirection} on:change={load}>
          <option value="tutte">{$t('scadenzario.filters.directionAll')}</option>
          <option value="incassi">{$t('scadenzario.filters.directionIncassi')}</option>
          <option value="pagamenti">{$t('scadenzario.filters.directionPagamenti')}</option>
        </select>
      </div>
      <div>
        <label class="label" for="sc-status">{$t('scadenzario.filters.status')}</label>
        <select id="sc-status" class="input w-full" bind:value={filterStatus} on:change={load}>
          <option value="aperte">{$t('scadenzario.filters.statusAperte')}</option>
          <option value="scadute">{$t('scadenzario.filters.statusScadute')}</option>
          <option value="inScadenza">{$t('scadenzario.filters.statusInScadenza')}</option>
          <option value="programmate">{$t('scadenzario.filters.statusProgrammate')}</option>
          <option value="saldate">{$t('scadenzario.filters.statusSaldate')}</option>
          <option value="tutte">{$t('scadenzario.filters.statusTutte')}</option>
        </select>
      </div>
      <div>
        <label class="label" for="sc-from">{$t('scadenzario.filters.from')}</label>
        <input id="sc-from" type="date" class="input w-full" bind:value={filterFrom} on:change={load} />
      </div>
      <div>
        <label class="label" for="sc-to">{$t('scadenzario.filters.to')}</label>
        <input id="sc-to" type="date" class="input w-full" bind:value={filterTo} on:change={load} />
      </div>
      <div>
        <label class="label" for="sc-cp">{$t('scadenzario.filters.counterparty')}</label>
        <input id="sc-cp" type="search" class="input w-full" bind:value={filterCounterparty}
          placeholder={$t('scadenzario.filters.counterpartyPlaceholder')} on:change={load} />
      </div>
    </div>
    <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
      <p class="text-xs text-muted">{$t('scadenzario.filters.rangeHint')}</p>
      <button type="button" class="btn btn-secondary btn-sm" on:click={resetFilters}>
        {$t('scadenzario.filters.reset')}
      </button>
    </div>
  </div>

  {#if loading}
    <PageLoader />
  {:else if error}
    <ErrorBanner {error} title={$t('scadenzario.errorTitle')} />
  {:else if data}
    {#if notice === 'totalsPartial'}
      <div class="alert alert-danger mb-6" role="alert">
        <AlertTriangle size={18} class="alert-mark" />
        <p class="alert-message">{$t('scadenzario.totalsPartial')}</p>
      </div>
    {:else if notice === 'listCut'}
      <div class="alert alert-warning mb-6" role="status">
        <AlertTriangle size={18} class="alert-mark" />
        <p class="alert-message">{$t('scadenzario.truncated')}</p>
      </div>
    {/if}

    {#each visibleSides as side}
      {@const rows = bySide[side]}
      {@const sideTotals = totals?.[side]}
      <section class="mb-12" aria-labelledby="scadenzario-{side}">
        <div class="section-heading">
          <h2 id="scadenzario-{side}" class="inline-flex items-center gap-2">
            {$t(`scadenzario.sections.${side}`)}
            <InfoHint text={$t(`scadenzario.sections.${side}Hint`)} />
          </h2>
        </div>

        <!-- Totals in money. Row counts alone never told anyone whether the
             month works out. -->
        {#if sideTotals}
          <dl class="summary-strip">
            {#each ['scadute', 'inScadenza', 'programmate', 'saldate', 'aperte'] as bucket}
              <div class="summary-item">
                <dt class="summary-label flex items-center gap-1">
                  {$t(`scadenzario.totals.${bucket}`)}
                  <InfoHint text={bucket === 'aperte'
                    ? $t('scadenzario.totals.aperteHint')
                    : $t(`scadenzario.bucketHelp.${bucket}`, { days: dueSoonDays })} />
                </dt>
                <dd class="summary-value" class:text-danger={bucket === 'scadute' && sideTotals[bucket].count > 0}>
                  {formatEuro(sideTotals[bucket].importo)}
                </dd>
                <dd class="summary-note">
                  {sideTotals[bucket].count === 1
                    ? $t('scadenzario.totals.countOne')
                    : $t('scadenzario.totals.count', { count: sideTotals[bucket].count })}
                </dd>
              </div>
            {/each}
          </dl>
          <!-- Said only where a credit note is actually on screen: the totals
               are lower than the rate above them add up to, and the reason is
               a row the reader can see. -->
          {#if rows.some((item) => item.notaDiCredito)}
            <p class="-mt-3 mb-4 text-xs text-accent">
              {$t('scadenzario.totals.creditNotes')}
            </p>
          {/if}
        {/if}

        {#if rows.length === 0}
          <EmptyState icon={CalendarClock}
            title={$t('scadenzario.empty.title')}
            body={filterStatus === 'aperte' && !filterFrom && !filterTo && !filterCounterparty
              ? $t('scadenzario.empty.body')
              : $t('scadenzario.empty.filtered')} />
        {:else}
          <ul class="schedule-list">
            {#each rows as item (item.id)}
              <li>
                <div class="schedule-row">
                  <!-- Due date + urgency. First on every screen width: it is
                       the column the page is sorted and reasoned about by. -->
                  <!-- The badge explanations are the page's teaching material,
                       so they hang off a real tappable control: a `title` never
                       appears on a phone, which is where the window the "in
                       scadenza" badge stands for was explained and nowhere
                       else. -->
                  <div class="schedule-when flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span class="cell-strong">{formatDate(item.dataScadenza)}</span>
                    <span class="inline-flex items-center gap-1">
                      <span class="badge {BUCKET_BADGE[item.bucket]}">
                        {$t(`scadenzario.buckets.${item.bucket}`)}
                      </span>
                      <InfoHint text={$t(`scadenzario.bucketHelp.${item.bucket}`, { days: dueSoonDays })} />
                    </span>
                    {#if item.parzialmentePagata}
                      <span class="inline-flex items-center gap-1">
                        <span class="badge badge-yellow">{$t('scadenzario.row.partial')}</span>
                        <InfoHint text={$t('scadenzario.row.partialHelp')} />
                      </span>
                    {/if}
                    <!-- A credit note's rata is the only row on the page whose
                         amount is negative. Named and explained here, because a
                         minus sign with nothing next to it reads as a bug on a
                         page about money owed. -->
                    {#if item.notaDiCredito}
                      <span class="inline-flex items-center gap-1">
                        <span class="badge badge-magenta">{$t('scadenzario.row.creditNote')}</span>
                        <InfoHint text={$t('scadenzario.row.creditNoteHelp')} />
                      </span>
                    {/if}
                  </div>

                  <div class="schedule-what">
                    <a href={`/invoices/${item.invoiceId}`} use:link class="invoice-link break-words">
                      {invoiceLabel(item)}
                    </a>
                    <span class="text-xs text-muted ml-2">
                      {$t('scadenzario.row.rata', { numero: item.numeroTranche })}
                    </span>
                    <p class="text-xs text-muted break-words">{counterpartyLabel(item)}</p>
                  </div>

                  <div class="schedule-amount">
                    <p class="cell-strong tabular">
                      {formatEuro((item.segno ?? 1) * (item.saldata ? item.pagato : item.residuo))}
                    </p>
                    <p class="text-xs text-muted">{timingLabel(item)}</p>
                    {#if item.notaDiCredito}
                      <!-- Which way the refund goes depends on the side: on an
                           invoice we issued we give the money back, on one we
                           received we get it back. -->
                      <p class="text-xs text-accent">
                        {item.tipo === 'incasso'
                          ? $t('scadenzario.row.refundIncasso')
                          : $t('scadenzario.row.refundPagamento')}
                      </p>
                    {/if}
                    {#if item.parzialmentePagata}
                      <!-- The amount above is what is missing; without this line
                           a part-paid row is indistinguishable from an
                           untouched one worth less. -->
                      <p class="text-xs text-warning">
                        {$t('scadenzario.row.alreadyPaid', {
                          importo: formatEuro(item.pagato), totale: formatEuro(item.importo),
                        })}
                      </p>
                    {/if}
                  </div>

                  <!-- Offered for as long as anything is still owed: a part
                       payment leaves a residuo, and hiding the action then
                       would leave no way at all to record the balance. -->
                  {#if $canWriteInCompany && !item.saldata}
                    <div class="schedule-action">
                      <button type="button" class="btn btn-secondary btn-sm w-full drawer:w-auto"
                        on:click={() => startPaying(item)}>
                        <Check size={14} class="mr-1" />
                        {side === 'incassi'
                          ? $t('scadenzario.actions.markPaidIncasso')
                          : $t('scadenzario.actions.markPaidPagamento')}
                      </button>
                    </div>
                  {/if}
                </div>

                {#if payingId === item.id}
                  <!-- Inline, on the row itself: the amount defaults to what is
                       still owed and stays editable, because a part payment is
                       the normal case this page has to record. -->
                  <div class="next-step mb-3">
                    <ErrorBanner error={payError} />
                    <div class="flex flex-wrap items-end gap-3">
                      <div class="w-full sm:w-44">
                        <label class="label" for={`pay-date-${item.id}`}>
                          {$t('scadenzario.markPaid.dateLabel')}
                        </label>
                        <input id={`pay-date-${item.id}`} type="date" class="input w-full" bind:value={payDate} />
                      </div>
                      <div class="w-full sm:w-44">
                        <label class="label" for={`pay-amount-${item.id}`}>
                          {item.parzialmentePagata
                            ? $t('scadenzario.markPaid.amountLabelAdd')
                            : $t('scadenzario.markPaid.amountLabel')}
                        </label>
                        <input id={`pay-amount-${item.id}`} type="number" step="0.01" min="0"
                          class="input w-full" bind:value={payAmount} />
                      </div>
                      <div class="flex gap-2">
                        <button type="button" class="btn btn-primary btn-sm" disabled={paySaving}
                          on:click={() => confirmPaying(item)}>
                          {paySaving ? $t('scadenzario.markPaid.saving') : $t('scadenzario.actions.confirm')}
                        </button>
                        <button type="button" class="btn btn-secondary btn-sm" disabled={paySaving}
                          on:click={cancelPaying}>
                          {$t('scadenzario.actions.cancel')}
                        </button>
                      </div>
                    </div>
                    <p class="mt-2 text-xs text-muted">
                      {item.parzialmentePagata
                        ? $t('scadenzario.markPaid.amountHintPartial', { pagato: formatEuro(item.pagato) })
                        : $t('scadenzario.markPaid.amountHint')}
                    </p>
                  </div>
                {/if}
              </li>
            {/each}
          </ul>
        {/if}
      </section>
    {/each}
  {/if}
</Layout>
